import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { maskPhone } from "@/lib/phone";
import { toast } from "sonner";
import { formatCurrency } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, X, Search, Download } from "lucide-react";

/* Advance Patient Search.
 *
 * One text box answers "where is Mrs Shah". It cannot answer "everyone in
 * 400050 with an email", "everyone turning 40 this month", or "every patient we
 * hold no phone number for" — which is what reception actually asks when they
 * are pulling a list to call or a campaign to send.
 *
 * The operator list mirrors the backend's closed allow-list exactly; anything
 * else is rejected there, so this UI can never construct a query the server
 * will not accept. */

const FIELDS = [
  { key: "name", label: "Patient name", type: "text" },
  { key: "mobile", label: "Mobile", type: "text" },
  { key: "alt_mobile", label: "Alternate mobile", type: "text" },
  { key: "email", label: "Email", type: "text" },
  { key: "patient_no", label: "Patient ID", type: "text" },
  { key: "gender", label: "Gender", type: "select", options: ["Female", "Male", "Other"] },
  { key: "age", label: "Age", type: "number" },
  { key: "dob", label: "Date of birth", type: "date" },
  { key: "blood_group", label: "Blood group", type: "text" },
  { key: "city", label: "City", type: "text" },
  { key: "address", label: "Street address", type: "text" },
  { key: "pincode", label: "Pincode", type: "text" },
  { key: "occupation", label: "Occupation", type: "text" },
  { key: "language", label: "Language", type: "text" },
  { key: "referred_by", label: "Referred by", type: "text" },
  { key: "groups", label: "Group", type: "text" },
  { key: "source", label: "Source", type: "text" },
  { key: "total_revenue", label: "Lifetime value", type: "number" },
];

const OPS = {
  text: [["contains", "contains"], ["is", "is exactly"], ["starts_with", "starts with"],
         ["has", "is present"], ["not_has", "is missing"]],
  number: [["is", "is"], ["gt", "is more than"], ["lt", "is less than"],
           ["between", "is between"], ["has", "is present"], ["not_has", "is missing"]],
  date: [["is", "is"], ["gt", "is after"], ["lt", "is before"],
         ["in_month", "birthday is in month"], ["has", "is present"], ["not_has", "is missing"]],
  select: [["is", "is"], ["has", "is present"], ["not_has", "is missing"]],
};

const NO_VALUE = new Set(["has", "not_has"]);

const MONTHS = ["January", "February", "March", "April", "May", "June",
                "July", "August", "September", "October", "November", "December"];

export default function AdvancedPatientSearch({ open, onClose }) {
  const navigate = useNavigate();
  const [rows, setRows] = useState([{ field: "city", op: "contains", value: "" }]);
  const [match, setMatch] = useState("all");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  const fieldMeta = (key) => FIELDS.find((f) => f.key === key) || FIELDS[0];

  const setRow = (i, patch) => setRows((rs) => rs.map((r, idx) => {
    if (idx !== i) return r;
    const next = { ...r, ...patch };
    // Changing the field can strand an operator the new type does not offer.
    if (patch.field) {
      const allowed = OPS[fieldMeta(patch.field).type].map(([o]) => o);
      if (!allowed.includes(next.op)) next.op = allowed[0];
      next.value = "";
    }
    return next;
  }));

  const run = async () => {
    setBusy(true);
    try {
      const criteria = rows
        .filter((r) => NO_VALUE.has(r.op) || String(r.value || "").trim() !== "")
        .map((r) => ({ field: r.field, op: r.op, value: NO_VALUE.has(r.op) ? null : String(r.value) }));
      if (!criteria.length) { toast.error("Add at least one condition"); setBusy(false); return; }
      const { data } = await api.post("/patients/search", { criteria, match, limit: 500 });
      setResult(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const exportCsv = () => {
    const cols = [["patient_no", "Patient ID"], ["name", "Name"], ["mobile", "Mobile"],
                  ["email", "Email"], ["gender", "Gender"], ["age", "Age"], ["city", "City"]];
    const head = cols.map(([, l]) => l).join(",");
    const body = (result.items || []).map((p) => cols.map(([k]) => {
      const s = p[k] == null ? "" : String(p[k]);
      return s.includes(",") ? `"${s.replace(/"/g, '""')}"` : s;
    }).join(",")).join("\n");
    const blob = new Blob([`${head}\n${body}`], { type: "text/csv;charset=utf-8;" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `patient-search-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  };

  const valueInput = (r, i) => {
    if (NO_VALUE.has(r.op)) return <span className="text-xs italic text-muted-foreground">no value needed</span>;
    const meta = fieldMeta(r.field);
    if (r.op === "in_month") {
      return (
        <Select value={r.value || "1"} onValueChange={(v) => setRow(i, { value: v })}>
          <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
          <SelectContent>
            {MONTHS.map((m, idx) => <SelectItem key={m} value={String(idx + 1)}>{m}</SelectItem>)}
          </SelectContent>
        </Select>
      );
    }
    if (meta.type === "select") {
      return (
        <Select value={r.value || meta.options[0]} onValueChange={(v) => setRow(i, { value: v })}>
          <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
          <SelectContent>{meta.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
        </Select>
      );
    }
    return (
      <Input className="h-9" type={meta.type === "date" ? "date" : meta.type === "number" ? "number" : "text"}
             placeholder={r.op === "between" ? "e.g. 30,45" : ""}
             value={r.value || ""} onChange={(e) => setRow(i, { value: e.target.value })} />
    );
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle className="font-display text-navy">Advanced patient search</DialogTitle>
        </DialogHeader>

        <div className="space-y-2">
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Match</span>
            <Select value={match} onValueChange={setMatch}>
              <SelectTrigger className="h-8 w-36"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">all conditions</SelectItem>
                <SelectItem value="any">any condition</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] items-center gap-2">
              <Select value={r.field} onValueChange={(v) => setRow(i, { field: v })}>
                <SelectTrigger className="h-9" data-testid={`search-field-${i}`}><SelectValue /></SelectTrigger>
                <SelectContent>
                  {FIELDS.map((f) => <SelectItem key={f.key} value={f.key}>{f.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={r.op} onValueChange={(v) => setRow(i, { op: v, value: NO_VALUE.has(v) ? "" : r.value })}>
                <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {OPS[fieldMeta(r.field).type].map(([o, l]) => <SelectItem key={o} value={o}>{l}</SelectItem>)}
                </SelectContent>
              </Select>
              {valueInput(r, i)}
              <button onClick={() => setRows((rs) => rs.filter((_, idx) => idx !== i))}
                disabled={rows.length === 1}
                className="rounded p-1.5 text-muted-foreground hover:bg-red-50 hover:text-red-600 disabled:opacity-30">
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}

          <div className="flex items-center justify-between pt-1">
            <Button variant="outline" size="sm"
              onClick={() => setRows((rs) => [...rs, { field: "name", op: "contains", value: "" }])}>
              <Plus className="mr-1 h-3.5 w-3.5" /> Add condition
            </Button>
            <Button onClick={run} disabled={busy} className="bg-navy text-white hover:bg-navy-hover"
                    data-testid="run-search-btn">
              <Search className="mr-1.5 h-4 w-4" /> {busy ? "Searching…" : "Search"}
            </Button>
          </div>
        </div>

        {result && (
          <div className="mt-2 space-y-2 border-t border-navy/10 pt-3">
            <div className="flex items-center justify-between">
              <p className="text-sm">
                <span className="font-display text-xl font-semibold text-navy">{result.total}</span>
                <span className="text-muted-foreground">
                  {" "}patient{result.total === 1 ? "" : "s"}
                  {result.returned < result.total && ` — showing the first ${result.returned}`}
                </span>
              </p>
              {result.items?.length > 0 && (
                <Button size="sm" variant="outline" onClick={exportCsv}>
                  <Download className="mr-1.5 h-3.5 w-3.5" /> CSV
                </Button>
              )}
            </div>
            <div className="max-h-72 overflow-y-auto rounded-lg border border-navy/10">
              <table className="w-full text-sm">
                <tbody className="divide-y divide-navy/5">
                  {(result.items || []).map((p) => (
                    <tr key={p.id} className="cursor-pointer hover:bg-nude/30"
                        onClick={() => { onClose(); navigate(`/patients/${p.id}`); }}
                        data-testid="search-result-row">
                      <td className="px-3 py-1.5 font-mono text-xs text-muted-foreground">{p.patient_no}</td>
                      <td className="px-3 py-1.5 font-medium text-ink">{p.name}</td>
                      <td className="px-3 py-1.5 text-xs text-muted-foreground">{maskPhone(p.mobile)}</td>
                      <td className="px-3 py-1.5 text-xs text-muted-foreground">{p.city}</td>
                      <td className="px-3 py-1.5 text-right text-xs text-muted-foreground">
                        {formatCurrency(p.total_revenue || 0)}
                      </td>
                    </tr>
                  ))}
                  {result.items?.length === 0 && (
                    <tr><td className="px-3 py-8 text-center text-sm text-muted-foreground">No matches.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
